import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import OCRPatente from '../components/OCRPatente';
import { api, apiUrl } from '../api/client';
import { useAuth } from '../stores/auth';

type TipoInfraccion = {
  id: number;
  codigo: string;
  descripcion: string;
  monto_centavos: number;
  permite_pronto_pago: boolean;
  descuento_pronto_pago_porc: number;
  dias_pronto_pago: number;
};

// ─── Slot de foto adicional (sin OCR) ───────────────────────
function FotoSlot({ file, onAdd, onDelete }: {
  file: File | null;
  onAdd: (f: File) => void;
  onDelete: () => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    if (!file) { setPreview(null); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <div className="relative">
      {preview ? (
        <>
          <img
            src={preview}
            alt="Foto adjunta"
            className="w-full h-28 object-cover rounded-xl border border-gray-200"
          />
          <button
            type="button"
            onClick={onDelete}
            className="absolute top-1 right-1 bg-red-600 text-white w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold shadow-md"
            aria-label="Eliminar foto"
          >✕</button>
        </>
      ) : (
        <>
          <button
            type="button"
            onClick={() => ref.current?.click()}
            className="w-full h-28 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-1 text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="text-2xl">📷</span>
            <span className="text-xs font-medium">Agregar foto</span>
          </button>
          <input
            ref={ref}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onAdd(f);
              e.target.value = '';
            }}
          />
        </>
      )}
    </div>
  );
}

// ─── Página principal ────────────────────────────────────────
export default function LabrarActa() {
  const nav = useNavigate();
  const loc = useLocation() as { state?: { patente?: string; sesion_id?: number | null } };
  const token = useAuth((s) => s.accessToken);

  const [patente, setPatente] = useState(loc.state?.patente || '');
  const [tipos, setTipos] = useState<TipoInfraccion[]>([]);
  const [tipoId, setTipoId] = useState<number | null>(null);
  const [direccion, setDireccion] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [lat, setLat] = useState<number | null>(null);
  const [lng, setLng] = useState<number | null>(null);

  // 3 slots: [0] = foto OCR, [1] y [2] = fotos adicionales
  const [fotos, setFotos] = useState<(File | null)[]>([null, null, null]);
  const fotoCount = fotos.filter(Boolean).length;

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const t = await api<TipoInfraccion[]>('/inspector/tipos-infraccion');
        setTipos(t);
        if (t.length > 0) setTipoId(t[0].id);
      } catch (e: any) {
        setErr('Error cargando tipos: ' + (e.message || e));
      }
    })();

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (p) => { setLat(p.coords.latitude); setLng(p.coords.longitude); },
        () => { /* ignore */ },
        { timeout: 5000, maximumAge: 60000 }
      );
    }
  }, []);

  function setFoto(idx: number, file: File | null) {
    setFotos((prev) => { const n = [...prev]; n[idx] = file; return n; });
  }

  async function subirFoto(file: File): Promise<string | null> {
    const fd = new FormData();
    fd.append('archivo', file, 'acta.jpg');
    const res = await fetch(apiUrl('/inspector/foto'), {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    });
    const json = await res.json();
    if (!json.ok) throw new Error(json.error || 'Error subiendo foto');
    return json.data?.url || null;
  }

  async function labrar() {
    if (patente.length < 6) return setErr('Patente inválida');
    if (!tipoId) return setErr('Elegí un tipo de infracción');

    setLoading(true);
    setErr(null);
    try {
      const fotosUrls: string[] = [];
      for (const f of fotos) {
        if (!f) continue;
        const url = await subirFoto(f);
        if (url) fotosUrls.push(url);
      }

      const infr = await api<any>('/inspector/labrar', {
        method: 'POST',
        body: JSON.stringify({
          tipo_id: tipoId,
          patente,
          direccion,
          observaciones,
          latitud: lat,
          longitud: lng,
          est_sesion_anulada_id: loc.state?.sesion_id ?? null,
          fotos: fotosUrls,
        }),
      });
      nav('/confirmar', { state: { acta: infr } });
    } catch (e: any) {
      setErr(e.message || 'Error labrando');
    } finally {
      setLoading(false);
    }
  }

  const tipoSel = tipos.find((t) => t.id === tipoId);

  return (
    <>
      <TopBar title="Labrar acta" back />
      <main className="px-4 py-5 space-y-4 max-w-md mx-auto pb-24">

        {/* Patente con OCR */}
        <OCRPatente
          value={patente}
          onChange={setPatente}
          onPhoto={(f) => setFoto(0, f instanceof File ? f : new File([f], 'acta.jpg', { type: f.type }))}
          onDeletePhoto={() => setFoto(0, null)}
        />

        {/* Fotos adicionales (slots 1 y 2) */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2">
            Fotos de la infracción
            <span className="ml-2 font-normal text-gray-400">({fotoCount}/3)</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <FotoSlot
              file={fotos[1]}
              onAdd={(f) => setFoto(1, f)}
              onDelete={() => setFoto(1, null)}
            />
            <FotoSlot
              file={fotos[2]}
              onAdd={(f) => setFoto(2, f)}
              onDelete={() => setFoto(2, null)}
            />
          </div>
          {fotos[0] && (
            <p className="text-xs text-gray-400 mt-1">
              + la foto del escáner de patente se adjunta automáticamente.
            </p>
          )}
        </div>

        {/* Tipo de infracción */}
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2">Tipo de infracción</label>
          <div className="space-y-2">
            {tipos.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => setTipoId(t.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                  tipoId === t.id
                    ? 'bg-brand-50 border-brand-500 text-gray-900'
                    : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                }`}
              >
                <span className={`w-4 h-4 rounded-full border-2 flex-shrink-0 ${
                  tipoId === t.id ? 'border-brand-500 bg-brand-500' : 'border-gray-300'
                }`} />
                <span className="flex-1 text-sm font-semibold">
                  {t.codigo} — ${(t.monto_centavos / 100).toFixed(2)}
                </span>
              </button>
            ))}
          </div>
          {tipoSel && (
            <p className="text-xs text-gray-500 mt-2">{tipoSel.descripcion}</p>
          )}
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Dirección</label>
          <input
            type="text"
            value={direccion}
            onChange={(e) => setDireccion(e.target.value)}
            placeholder="Av. San Martín 1234"
            className="w-full bg-white border border-gray-300 rounded-xl px-3 py-3 text-sm text-gray-900 focus:border-brand-500 outline-none"
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1">Observaciones (opcional)</label>
          <textarea
            value={observaciones}
            onChange={(e) => setObservaciones(e.target.value)}
            rows={3}
            className="w-full bg-white border border-gray-300 rounded-xl px-3 py-2 text-sm text-gray-900 focus:border-brand-500 outline-none"
          />
        </div>

        {lat !== null && (
          <p className="text-xs text-gray-400">📍 GPS capturado · {lat.toFixed(5)}, {lng?.toFixed(5)}</p>
        )}

        {err && (
          <div className="bg-red-50 border border-red-300 text-red-700 text-sm rounded-xl p-3">
            {err}
          </div>
        )}

        <button
          onClick={labrar}
          disabled={loading}
          className="w-full bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-xl py-4 font-bold text-base"
        >
          {loading ? 'Labrando…' : 'Confirmar y labrar acta'}
        </button>
      </main>
    </>
  );
}
